/**
 * Document folder manager — add / rename / reorder / re-parent / deactivate
 * the folders an app's documents are filed into.
 *
 * ── Shape (2026-08-17) ────────────────────────────────────────────────
 * ADD is the modal's job; the catalogue is folded away behind a labelled
 * disclosure, and a folder becomes editable one at a time via its ✎. It
 * used to open on every folder at once as live inputs — thirteen rows of
 * name field, number field and three buttons — which is a lot of live
 * edits to put in front of somebody whose errand was "add a folder".
 *
 * ⚠️ Nothing was removed: the list is one click away and its summary says
 * how many folders are in it. A count of zero says so too, because "no
 * folders yet" is information and an empty box is not.
 *
 * Six apps need this identical modal. quality, safety and marketing each
 * grew their own copy; rather than add three more, it lives here. The
 * copies are deliberately left in place for now — they work, and a
 * six-app sweep of live admin UI deserves its own verification pass.
 *
 * The API still calls these "categories" (`<app>_document_categories`,
 * `documents.category`). Renaming the column across six apps plus the
 * intranet proxy is a data migration that buys nothing a user can see, so
 * the vocabulary is translated at the UI boundary only — here.
 *
 * ── Nesting (`nesting: true`, 2026-08-17) ─────────────────────────────
 * Adds a parent control to each row and to the add form, and draws the
 * list as a tree. OFF by default: the app's backend must accept
 * `parent_slug` on POST and PUT and validate it, and a control that
 * always 400s is worse than no control. Only isg-hr has that today.
 *
 * 🔴 A folder is NEVER offered itself or one of its own descendants as a
 * parent. The server refuses both, but the refusal is a round trip and a
 * red message for a move the user was never able to want — and the one
 * that matters, "move this under its own child", detaches the whole
 * branch from the root if it ever lands.
 */
import { api } from '../services/api.js';
import { getIcon } from './navigation.js';
import { t } from '../i18n/index.js';

/**
 * The default audience countries, labelled in the reader's language.
 * A FUNCTION, not a module-scope array: t() at import time would freeze the
 * labels in whatever locale was active before the app's catalogue loaded.
 * Shared with doc-admin so the two audience controls can never disagree.
 */
export function defaultCountries() {
  return [
    { value: 'GB', label: t('countries.gb') },
    { value: 'US', label: t('countries.us') },
    { value: 'DE', label: t('countries.de') },
    { value: 'CZ', label: t('countries.cz') },
  ];
}

/** Audience condition types — the value is the stored slug, the label translated. */
export function conditionTypes() {
  return [
    { value: 'company', label: t('filters.company') },
    { value: 'country', label: t('filters.country') },
    { value: 'department', label: t('filters.department') },
  ];
}

function esc(s) {
  return String(s == null ? '' : s)
    .replace(/&/g, '&amp;').replace(/</g, '&lt;')
    .replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

/**
 * @param {Object}   opts
 * @param {string}   opts.basePath       — CRUD base, e.g. '/admin/documents/categories'.
 *                                         GET lists ALL (incl. inactive), POST creates,
 *                                         PUT/DELETE take /<id>.
 * @param {boolean}  [opts.nesting]      — offer a parent folder control (default false)
 * @param {string}   [opts.defaultParent]— slug the Add form starts on, so opening the
 *                                         manager from inside a folder means
 *                                         "add a subfolder here"
 * @param {Function} [opts.onClose]      — called after the modal closes (refresh your list)
 * @param {string}   [opts.title]
 */
export function showFolderManager({
  basePath, onClose, title = t('folders.title'),
  nesting = false, defaultParent = null, audience = null,
}) {
  // ── Folder audience (BETA-00231) ────────────────────────────────────
  //
  // Naomi, 2026-08-18: *"could we do that for folders, so that we don't have
  // to set the preferences for each document within the folder?"*
  //
  // 🔴 It is a DEFAULT, not a second gate — the wording below says so on
  // screen, because the whole risk of this feature is somebody assuming a
  // folder rule overrides a document's own and quietly mis-scoping a file.
  // A document that carries its own audience is untouched by its folder's.
  //
  // The control is deliberately identical to the document one: same four
  // types, same slug-not-id rule, same "no conditions = everyone" sentence.
  // A folder audience is the estate's existing concept attached to a
  // different row, not a new idea with its own vocabulary.
  const audienceOn = Boolean(audience);
  const companies = (audience && audience.companies) || [];
  const departments = (audience && audience.departments) || [];
  const countries = (audience && audience.countries) || defaultCountries();
  const COND_TYPES = conditionTypes();
  const typeLabel = (type) => (COND_TYPES.find((ct) => ct.value === type) || {}).label || type;
  // Pending edits, keyed by folder id — so adding two rules before pressing
  // Save does not lose the first.
  let draftConds = null;

  function condOptions(type) {
    // 🔴 The SLUG (or department NAME), never the numeric id — a company
    // that is deleted and recreated silently darkens every row conditioned
    // on its old id. Same rule as the document audience builder.
    if (type === 'company') {
      return companies.map((c) => ({ value: String(c.slug || c.id), label: c.name }));
    }
    if (type === 'country') return countries;
    return departments.map((d) => ({ value: String(d.name || d.id), label: d.name }));
  }

  function audienceHtml(c) {
    if (!audienceOn) return '';
    const conds = draftConds || (c.conditions || []).map(
      (x) => ({ type: x.type, value: x.value }));
    const type = (draftConds && draftConds._type) || 'company';
    const chips = conds.length
      ? conds.map((x, i) => `<span class="cond-badge ${esc(x.type)}" style="display:inline-flex;align-items:center;gap:4px;">
            ${esc(typeLabel(x.type))}: ${esc(x.value)}
            <button type="button" data-fm-cond-rm="${i}" title="${esc(t('common.remove'))}"
              style="border:0;background:none;cursor:pointer;color:inherit;font-size:1rem;line-height:1;">&times;</button>
          </span>`).join(' ')
      : `<span style="font-size:var(--text-xs);color:var(--text-tertiary);">${esc(t('docs.everyone'))}</span>`;
    return `
      <div style="flex-basis:100%;border-top:1px dashed var(--border-secondary);padding-top:var(--space-2);margin-top:var(--space-1);">
        <div style="font-size:var(--text-xs);color:var(--text-tertiary);margin-bottom:4px;">
          ${esc(t('folders.audience_hint'))}
        </div>
        <div style="display:flex;gap:4px;flex-wrap:wrap;align-items:center;margin-bottom:var(--space-2);">${chips}</div>
        <div style="display:flex;gap:var(--space-2);flex-wrap:wrap;">
          <select class="form-input" data-fm-cond-type style="flex:0 0 8rem;font-size:var(--text-sm);padding:4px 8px;">
            ${COND_TYPES.map((ct) => `<option value="${ct.value}"${type === ct.value ? ' selected' : ''}>${esc(ct.label)}</option>`).join('')}
          </select>
          <select class="form-input" data-fm-cond-value style="flex:1;min-width:8rem;font-size:var(--text-sm);padding:4px 8px;">
            ${condOptions(type).map((o) => `<option value="${esc(o.value)}">${esc(o.label)}</option>`).join('')}
          </select>
          <button type="button" class="btn btn-secondary btn-sm" data-fm-cond-add="${c.id}">${esc(t('common.add'))}</button>
        </div>
      </div>`;
  }
  // ── layout ──────────────────────────────────────────────────────────
  // 🔴 ADDING comes first and the catalogue is FOLDED AWAY behind a
  // disclosure. Reported 2026-08-17: "it makes no sense to see the full
  // list every time just to add a new category or folder." It was thirteen
  // rows of live inputs — a name field, a number field and three buttons
  // each — for a job that needs one field and a button, and every one of
  // those inputs was a live edit to a folder somebody's documents are
  // filed in. The list is one click away and says how many are in it, so
  // nothing is hidden; it just isn't the first thing the modal is about.
  //
  // ⚠️ <details> keeps its own open state, so `paint()` rewrites only the
  // rows inside it and an expanded list stays expanded across a save.
  const overlay = document.createElement('div');
  overlay.style.cssText = 'position:fixed;inset:0;background:var(--surface-overlay);z-index:1000;display:flex;align-items:center;justify-content:center;padding:var(--space-4);';
  overlay.innerHTML = `
    <div class="card" style="width:100%;max-width:${nesting ? '560px' : '460px'};max-height:80vh;overflow-y:auto;">
      <div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:var(--space-3);">
        <h2 style="margin:0;font-size:1.05rem;font-weight:600;">${esc(title)}</h2>
        <button id="fm-close" aria-label="${esc(t('common.close'))}" style="background:none;border:none;cursor:pointer;color:var(--text-tertiary);">${getIcon('x', 18)}</button>
      </div>

      <form id="fm-add" style="display:flex;gap:var(--space-2);flex-wrap:wrap;align-items:flex-start;">
        <input class="form-input" id="fm-name" placeholder="${esc(t('folders.new_placeholder'))}" style="flex:1;min-width:12rem;" required />
        ${nesting ? '<select class="form-input" id="fm-parent" style="flex:1;min-width:12rem;"></select>' : ''}
        <button type="submit" class="btn btn-primary btn-sm">${esc(t('common.add'))}</button>
      </form>
      <p style="font-size:var(--text-xs);color:var(--text-tertiary);margin:var(--space-2) 0 0;">
        ${esc(nesting ? t('folders.add_hint_nested') : t('folders.add_hint_flat'))}
      </p>

      <div id="fm-err" style="display:none;font-size:var(--text-xs);color:var(--red-600);margin-top:var(--space-2);"></div>

      <details id="fm-details" style="margin-top:var(--space-3);border-top:1px solid var(--border-secondary);padding-top:var(--space-3);">
        <summary style="cursor:pointer;font-size:var(--text-sm);color:var(--text-secondary);user-select:none;">
          <span id="fm-count">${esc(t('folders.existing'))}</span>
        </summary>
        <p style="font-size:var(--text-xs);color:var(--text-tertiary);margin:var(--space-2) 0;">
          ${esc(nesting ? t('folders.list_hint_nested') : t('folders.list_hint'))}
        </p>
        <div id="fm-list" style="display:grid;gap:2px;"></div>
      </details>
    </div>`;
  document.body.appendChild(overlay);

  const errEl = overlay.querySelector('#fm-err');
  const showErr = (msg) => {
    // Surface the SERVER's message — "3 document(s) are in this folder —
    // deactivate it instead" is the whole reason a delete was refused, and
    // a generic "Failed" hides it. The nesting refusals are the same:
    // "A folder cannot be moved inside one of its own subfolders" is the
    // entire explanation, and it only exists upstream.
    errEl.textContent = msg || t('common.something_went_wrong');
    errEl.style.display = 'block';
  };
  const clearErr = () => { errEl.style.display = 'none'; };

  const close = () => { overlay.remove(); if (onClose) onClose(); };
  overlay.querySelector('#fm-close').addEventListener('click', close);
  overlay.addEventListener('click', (e) => { if (e.target === overlay) close(); });

  // ── tree helpers ────────────────────────────────────────────────────
  // Folder identity is the slug where there is one, the id otherwise —
  // the same normalisation doc-admin makes, for the same reason (six apps
  // key on a slug, the intranet on an integer).
  const fid = (c) => String(c.slug != null ? c.slug : c.id);
  const parentOf = (c) => {
    if (c.parent_slug != null) return String(c.parent_slug);
    if (c.parent_id != null) return String(c.parent_id);
    return null;
  };

  /** Depth-first order with a depth on each row. Orphans (a parent that
   *  isn't in the list) are emitted at the ROOT rather than dropped — the
   *  same rule doc-admin renders by, and the reason is the same: a folder
   *  nobody draws is a folder that has vanished. */
  function ordered(cats) {
    const drawnParent = (c) => {
      const p = parentOf(c);
      return p && cats.some((x) => fid(x) === p) ? p : null;
    };
    const out = [];
    const seen = new Set();
    const walk = (parent, depth) => {
      cats.filter((c) => drawnParent(c) === parent).forEach((c) => {
        const id = fid(c);
        if (seen.has(id)) return;          // cycle-safe
        seen.add(id);
        out.push({ cat: c, depth });
        walk(id, depth + 1);
      });
    };
    walk(null, 0);
    // Belt and braces: anything a cycle kept out of the walk still gets a row.
    cats.forEach((c) => { if (!seen.has(fid(c))) out.push({ cat: c, depth: 0 }); });
    return out;
  }

  /** Every folder except `exclude` and everything beneath it. */
  function parentOptions(cats, exclude) {
    const banned = new Set();
    if (exclude != null) {
      banned.add(String(exclude));
      let grew = true;
      while (grew) {
        grew = false;
        cats.forEach((c) => {
          const p = parentOf(c);
          if (p && banned.has(p) && !banned.has(fid(c))) {
            banned.add(fid(c));
            grew = true;
          }
        });
      }
    }
    return ordered(cats)
      .filter(({ cat }) => !banned.has(fid(cat)))
      .map(({ cat, depth }) => ({
        value: fid(cat),
        // \u00A0 not a plain space: a plain space collapses inside
        // <option>, and the indent is what tells two folders with the
        // same name apart.
        label: `${'\u00A0'.repeat(depth * 4)}${depth ? '\u2514\u00A0' : ''}${cat.name}`,
      }));
  }

  function parentSelectHtml(cats, cat, selected) {
    const opts = parentOptions(cats, cat ? fid(cat) : null);
    const sel = selected == null ? '' : String(selected);
    return `<option value=""${sel === '' ? ' selected' : ''}>${esc(t('folders.top_level_option'))}</option>`
      + opts.map((o) => `<option value="${esc(o.value)}"${sel === o.value ? ' selected' : ''}>${esc(o.label)}</option>`).join('');
  }

  // Which folder's row is open for editing. ONE at a time — the wall of
  // simultaneously-editable rows is what this replaced, and it also meant
  // any row could be half-edited and abandoned while another was saved.
  let editingId = null;
  let lastCats = [];

  function readOnlyRow(c, depth) {
    return `
      <div style="display:flex;align-items:center;gap:var(--space-2);padding:6px 8px;border-radius:var(--radius-md);${depth ? `margin-left:${depth * 1.25}rem;` : ''}${c.is_active ? '' : 'opacity:0.55;'}">
        <span style="color:var(--text-tertiary);display:flex;">${getIcon('folder', 14)}</span>
        <span style="flex:1;min-width:0;font-size:var(--text-sm);overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">${esc(c.name)}</span>
        ${c.is_active ? '' : `<span style="font-size:var(--text-xs);color:var(--text-tertiary);">${esc(t('docs.inactive_folder'))}</span>`}
        <button data-fm-edit="${c.id}" title="${esc(t('folders.edit_named', { name: c.name }))}"
          style="background:none;border:none;cursor:pointer;color:var(--brand-600);display:flex;padding:2px;">${getIcon('edit', 15)}</button>
      </div>`;
  }

  function editRow(cats, c, depth) {
    return `
      <div style="display:flex;align-items:center;gap:var(--space-2);flex-wrap:wrap;padding:var(--space-2);border:1px solid var(--brand-600);border-radius:var(--radius-md);${depth ? `margin-left:${depth * 1.25}rem;` : ''}">
        <input class="form-input" data-fm-name="${c.id}" value="${esc(c.name)}"
          style="flex:1;min-width:8rem;font-size:var(--text-sm);padding:4px 8px;" />
        <input class="form-input" data-fm-order="${c.id}" type="number" value="${c.sort_order ?? 0}"
          title="${esc(t('folders.sort_order'))}" style="width:64px;font-size:var(--text-sm);padding:4px 8px;" />
        ${nesting ? `<label style="flex-basis:100%;display:flex;align-items:center;gap:var(--space-2);font-size:var(--text-xs);color:var(--text-tertiary);">
          ${esc(t('folders.inside'))}
          <select class="form-input" data-fm-parent="${c.id}" style="flex:1;font-size:var(--text-sm);padding:4px 8px;">
            ${parentSelectHtml(cats, c, parentOf(c))}
          </select>
        </label>` : ''}
        ${audienceHtml(c)}
        <div style="flex-basis:100%;display:flex;gap:var(--space-2);flex-wrap:wrap;">
          <button class="btn btn-primary btn-sm" data-fm-save="${c.id}">${esc(t('common.save'))}</button>
          <button class="btn btn-secondary btn-sm" data-fm-cancel="1">${esc(t('common.cancel'))}</button>
          <span style="flex:1;"></span>
          <button class="btn btn-secondary btn-sm" data-fm-toggle="${c.id}" data-active="${c.is_active ? '1' : ''}">${esc(c.is_active ? t('common.deactivate') : t('common.activate'))}</button>
          <button class="btn btn-secondary btn-sm" data-fm-del="${c.id}" title="${esc(t('folders.delete'))}" style="color:var(--red-600);">${getIcon('trash-2', 12)}</button>
        </div>
      </div>`;
  }

  async function paint() {
    let cats = [];
    try {
      cats = await api.get(basePath);
    } catch (err) {
      showErr(err && err.message);
      cats = [];
    }
    if (!Array.isArray(cats)) cats = cats.categories || [];
    lastCats = cats;

    const list = overlay.querySelector('#fm-list');
    const rows = nesting ? ordered(cats) : cats.map((c) => ({ cat: c, depth: 0 }));

    const label = overlay.querySelector('#fm-count');
    if (label) {
      const n = rows.length;
      label.textContent = n
        ? (nesting && rows.some((r) => r.depth)
          ? t('folders.existing_count_nested', { count: n })
          : t('folders.existing_count', { count: n }))
        : t('folders.none_yet');
    }

    list.innerHTML = rows.length
      ? rows.map(({ cat: c, depth }) => (String(c.id) === String(editingId)
        ? editRow(cats, c, depth)
        : readOnlyRow(c, depth))).join('')
      : `<p style="font-size:var(--text-sm);color:var(--text-tertiary);">${esc(t('folders.empty'))}</p>`;

    list.querySelectorAll('[data-fm-edit]').forEach((b) => b.addEventListener('click', () => {
      clearErr();
      // Drop any half-built audience from the row being left — carrying it
      // onto another folder would silently apply one folder's rules to a
      // different one.
      if (String(editingId) !== String(b.dataset.fmEdit)) draftConds = null;
      editingId = b.dataset.fmEdit;
      paint();
    }));

    list.querySelector('[data-fm-cancel]')?.addEventListener('click', () => {
      clearErr();
      editingId = null;
      draftConds = null;
      paint();
    });

    list.querySelectorAll('[data-fm-save]').forEach((b) => b.addEventListener('click', async () => {
      clearErr();
      const id = b.dataset.fmSave;
      const name = list.querySelector(`[data-fm-name="${id}"]`).value.trim();
      const sort = Number(list.querySelector(`[data-fm-order="${id}"]`).value) || 0;
      if (!name) return showErr(t('folders.name_required'));
      const body = { name, sort_order: sort };
      if (audienceOn) {
        // Sent on EVERY save, including as []. Absent means "leave alone"
        // upstream, so a folder can only be re-opened to everyone if the
        // empty list actually travels — same reasoning as `parent_slug`.
        body.conditions = (draftConds || []).map((x) => ({ type: x.type, value: x.value }));
      }
      if (nesting) {
        // Sent on EVERY save, including as null. `parent_slug` absent means
        // "leave it alone" upstream; a folder can only be moved back to the
        // top level if the empty choice actually travels.
        body.parent_slug = list.querySelector(`[data-fm-parent="${id}"]`).value || null;
      }
      try {
        await api.put(`${basePath}/${id}`, body);
        editingId = null;      // only on success — a refused save keeps the
        draftConds = null;     // row open with what the user typed still there
        paint();
      } catch (err) { showErr(err && err.message); }
    }));

    // ── folder audience chips ─────────────────────────────────────────
    const typeSel = list.querySelector('[data-fm-cond-type]');
    if (typeSel) {
      typeSel.addEventListener('change', () => {
        // Repaint only the VALUE list — repainting the row would drop the
        // name the user may have edited but not saved.
        const valSel = list.querySelector('[data-fm-cond-value]');
        valSel.innerHTML = condOptions(typeSel.value)
          .map((o) => `<option value="${esc(o.value)}">${esc(o.label)}</option>`).join('');
      });
    }
    list.querySelectorAll('[data-fm-cond-add]').forEach((b) => b.addEventListener('click', () => {
      const condType = list.querySelector('[data-fm-cond-type]').value;
      const v = list.querySelector('[data-fm-cond-value]').value;
      if (!v) return;
      const cat = (lastCats || []).find((x) => String(x.id) === String(b.dataset.fmCondAdd));
      draftConds = draftConds || (cat && cat.conditions || []).map(
        (x) => ({ type: x.type, value: x.value }));
      if (!draftConds.some((x) => x.type === condType && x.value === v)) draftConds.push({ type: condType, value: v });
      draftConds._type = condType;
      paint();
    }));
    list.querySelectorAll('[data-fm-cond-rm]').forEach((b) => b.addEventListener('click', () => {
      const cat = (lastCats || []).find((x) => String(x.id) === String(editingId));
      draftConds = draftConds || (cat && cat.conditions || []).map(
        (x) => ({ type: x.type, value: x.value }));
      draftConds.splice(Number(b.dataset.fmCondRm), 1);
      paint();
    }));

    list.querySelectorAll('[data-fm-toggle]').forEach((b) => b.addEventListener('click', async () => {
      clearErr();
      try {
        await api.put(`${basePath}/${b.dataset.fmToggle}`, { is_active: !b.dataset.active });
        editingId = null;
        paint();
      } catch (err) { showErr(err && err.message); }
    }));

    list.querySelectorAll('[data-fm-del]').forEach((b) => b.addEventListener('click', async () => {
      clearErr();
      if (!confirm(t('folders.confirm_delete'))) return;
      try {
        await api.delete(`${basePath}/${b.dataset.fmDel}`);
        editingId = null;
        paint();
      } catch (err) { showErr(err && err.message); }
    }));

    // The add form's parent list is rebuilt from the same data, so a folder
    // created a moment ago is immediately available to nest under.
    const addParent = overlay.querySelector('#fm-parent');
    if (addParent) {
      const keep = addParent.dataset.touched ? addParent.value : defaultParent;
      addParent.innerHTML = parentSelectHtml(cats, null, keep);
    }
  }

  overlay.querySelector('#fm-parent')?.addEventListener('change', (e) => {
    // Once the user has chosen, a repaint must not snap it back to the
    // folder they happened to open the manager from.
    e.target.dataset.touched = '1';
  });

  overlay.querySelector('#fm-add').addEventListener('submit', async (e) => {
    e.preventDefault();
    clearErr();
    const input = overlay.querySelector('#fm-name');
    const name = input.value.trim();
    if (!name) return;
    const body = { name };
    const parentEl = overlay.querySelector('#fm-parent');
    if (parentEl && parentEl.value) body.parent_slug = parentEl.value;
    try {
      await api.post(basePath, body);
      input.value = '';
      // Straight back into the field — adding several folders in a row is
      // the reason this form is at the top.
      input.focus();
      paint();
    } catch (err) { showErr(err && err.message); }
  });

  overlay.querySelector('#fm-name').focus();
  paint();
  return { close };
}
